import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'
import { WizardState, StudyPlan, DayPlan } from '../types/index'
import { generateMixedPlan, generateSeparatePlan, replanFromProgress } from '../lib/algorithm'
import { formatDate, formatMinutes, isToday } from '../lib/utils'
import { PremiumModal } from '../components/PremiumModal'
import { supabase } from '../lib/supabase'
import { downloadICS } from '../lib/ics'

export function PlanPage({ user, isPremium }: { user: any, isPremium: boolean }) {
  const { t, i18n } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const [plan, setPlan] = useState<StudyPlan | null>(null)
  const [showPremium, setShowPremium] = useState(false)
  const [progress, setProgress] = useState<Record<string, boolean>>({})
  const [saving, setSaving] = useState(false)
  const [editingPages, setEditingPages] = useState<{dayIndex: number, subIndex: number, examName: string} | null>(null)
  const [pagesInput, setPagesInput] = useState('')
  const [wizardStateRef, setWizardStateRef] = useState<any>(null)
  const [showNameModal, setShowNameModal] = useState(false)
  const [planName, setPlanName] = useState('')
  const [saved, setSaved] = useState(false)
  // isPremium viene da App.tsx

  useEffect(() => {
    const wizardState: WizardState = location.state?.wizardState
    if (!wizardState) { navigate('/'); return }
    const generated = wizardState.studyMode === 'separate'
      ? generateSeparatePlan(wizardState.exams, wizardState.restDays.weekDays, wizardState.restDays.specificDates)
      : generateMixedPlan(wizardState.exams, wizardState.restDays.weekDays, wizardState.restDays.specificDates)
    setPlan(generated)
    setWizardStateRef(wizardState)
  }, [])



  useEffect(() => {
    if (user?.id) {
      supabase.from('plans').select('progress').eq('user_id', user.id).order('updated_at', { ascending: false }).limit(1).single()
        .then(({ data }) => { if (data?.progress) setProgress(data.progress as any) })
    }
  }, [user?.id])

  useEffect(() => {
    if (user?.id) {
      supabase.from('plans').select('progress').eq('user_id', user.id).order('updated_at', { ascending: false }).limit(1).single()
        .then(({ data }) => { if (data?.progress) setProgress(data.progress as any) })
    }
  }, [user?.id])

  if (!plan) return <div style={{ padding: 32, textAlign: "center" }}>Caricamento...</div>

  const stressColor = plan.stressLevel === "verylow" ? "#EFF6FF" : plan.stressLevel === "low" ? "#DCFCE7" : plan.stressLevel === "medium" ? "#FEF9C3" : plan.stressLevel === "high" ? "#FEE2E2" : "#FCE7F3"
  const stressText = plan.stressLevel === "verylow" ? t("plan.verylow") : plan.stressLevel === "low" ? t("plan.low") : plan.stressLevel === "medium" ? t("plan.medium") : plan.stressLevel === "high" ? t("plan.high") : t("plan.veryhigh")
  const stressEmoji = plan.stressLevel === "verylow" ? "😴" : plan.stressLevel === "low" ? "✅" : plan.stressLevel === "medium" ? "⚡" : plan.stressLevel === "high" ? "🔥" : "💀"

  const handlePremiumAction = () => {
    if (!isPremium) { setShowPremium(true); return }
  }

  const handleSavePlan = async (name?: string) => {
    if (!user) { navigate('/auth'); return }
    if (!user) { window.location.href = '/auth'; return }
    if (!name) { setShowNameModal(true); return }
    setSaving(true)
    const wizardState = location.state?.wizardState
    const { error } = await supabase.from('plans').upsert({
      user_id: user.id,
      title: name || wizardState?.exams?.map((e: any) => e.name).join(', ') || 'Piano',
      wizard_state: wizardState,
      progress,
      updated_at: new Date().toISOString()
    })
    setSaving(false)
    if (error) alert('Errore: ' + error.message)
    else { setSaved(true); setTimeout(() => setSaved(false), 2500) }
  }

  const handleDownloadPDF = () => {
    if (!isPremium) { setShowPremium(true); return }
    if (!plan) return
    const ws = wizardStateRef || location.state?.wizardState
    const title = ws?.exams?.map((e: any) => e.name).join(' + ') || 'Piano di Studio'

    const rows = plan.days.map((day: any) => {
      const dateStr = new Date(day.date).toLocaleDateString('it-IT', { weekday: 'short', day: '2-digit', month: '2-digit' })
      if (day.isRestDay) return '<tr style="background:#F9FAFB"><td style="padding:8px 12px;color:#9CA3AF">' + dateStr + '</td><td colspan="4" style="padding:8px 12px;color:#9CA3AF">Giorno di riposo</td></tr>'
      if (day.isExamDay) return '<tr style="background:#1c3fff"><td style="padding:8px 12px;color:white;font-weight:bold">' + dateStr + '</td><td colspan="4" style="padding:8px 12px;color:white;font-weight:bold">ESAME: ' + day.examName + '</td></tr>'
      return day.subjects.map((sub: any, si: number) => '<tr style="border-bottom:1px solid #F3F4F6"><td style="padding:8px 12px;color:#6B7280;font-size:13px">' + (si === 0 ? dateStr : '') + '</td><td style="padding:8px 12px;color:' + (sub.color || '#1c3fff') + ';font-weight:600">' + sub.examName + '</td><td style="padding:8px 12px;font-size:13px">' + (sub.isRevision ? '<em>Ripasso</em>' : '<strong>p.' + sub.startPage + '-' + sub.endPage + '</strong> (' + sub.pages + ' pag.)') + '</td><td style="padding:8px 12px;color:#374151;font-size:13px">' + (sub.isRevision ? '' : sub.minutes + 'min') + '</td><td style="padding:8px 12px"><div style="background:#E5E7EB;border-radius:4px;height:8px;width:80px"><div style="background:' + (sub.color || '#1c3fff') + ';width:' + sub.progressPercent + '%;height:100%;border-radius:4px"></div></div><span style="font-size:11px;color:#6B7280;margin-left:4px">' + sub.progressPercent + '%</span></td></tr>').join('')
    }).join('')

    const html = '<!DOCTYPE html><html><head><meta charset="utf-8"><title>Piano di Studio</title><style>body{font-family:Arial,sans-serif;margin:0;padding:0}@media print{.no-print{display:none}}</style></head><body><div style="background:#1c3fff;padding:20px 30px;display:flex;align-items:center;justify-content:space-between"><div><div style="color:white;font-size:20px;font-weight:bold">Study Planner Schedule</div><div style="color:rgba(255,255,255,0.8);font-size:13px">Il tuo piano di studio personalizzato</div></div><div style="color:rgba(255,255,255,0.7);font-size:12px">Generato il ' + new Date().toLocaleDateString('it-IT') + '</div></div><div style="padding:24px 30px"><h2 style="color:#111827;margin:0 0 16px">Piano di Studio: ' + title + '</h2><table style="width:100%;border-collapse:collapse;border-radius:12px;overflow:hidden;border:1px solid #E5E7EB"><thead><tr style="background:#F9FAFB"><th style="padding:10px 12px;text-align:left;font-size:11px;color:#9CA3AF;text-transform:uppercase;letter-spacing:1px">Data</th><th style="padding:10px 12px;text-align:left;font-size:11px;color:#9CA3AF;text-transform:uppercase;letter-spacing:1px">Materia</th><th style="padding:10px 12px;text-align:left;font-size:11px;color:#9CA3AF;text-transform:uppercase;letter-spacing:1px">Pagine</th><th style="padding:10px 12px;text-align:left;font-size:11px;color:#9CA3AF;text-transform:uppercase;letter-spacing:1px">Tempo</th><th style="padding:10px 12px;text-align:left;font-size:11px;color:#9CA3AF;text-transform:uppercase;letter-spacing:1px">Progresso</th></tr></thead><tbody>' + rows + '</tbody></table></div><div style="background:#1c3fff;padding:12px 30px;text-align:center;color:white;font-size:12px">Study Planner Schedule — studyplannerschedule.com</div><div class="no-print" style="position:fixed;bottom:20px;right:20px;display:flex;gap:10px"><button onclick="window.print()" style="background:#1c3fff;color:white;border:none;padding:12px 24px;border-radius:10px;cursor:pointer;font-size:14px;font-weight:600">Stampa / Salva PDF</button><button onclick="window.close()" style="background:#F3F4F6;color:#374151;border:none;padding:12px 24px;border-radius:10px;cursor:pointer;font-size:14px">Chiudi</button></div></body></html>'

    const w = window.open('', '_blank')
    if (w) { w.document.write(html); w.document.close() }
  }

  const handleShare = async () => {
    if (!plan) return
    const ws = wizardStateRef || location.state?.wizardState
    const title = ws?.exams?.map((e: any) => e.name).join(' + ') || 'Piano di Studio'

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Piano di Studio - ' + title,
          text: 'Ho creato un piano di studio con Study Planner Schedule! Studia con me: ' + title,
          url: window.location.origin
        })
      } catch (err) {}
    } else {
      const text = 'Ho creato un piano di studio con Study Planner Schedule!\n' + title + '\n' + window.location.origin
      navigator.clipboard.writeText(text)
      alert('Testo copiato negli appunti!')
    }
  }

  const handleExportICS = () => {
    if (!isPremium) { setShowPremium(true); return }
    downloadICS(plan)
  }

  const handleReplan = (dayIndex: number, examName: string, pagesStudied: number) => {
    console.log('REPLAN:', dayIndex, examName, pagesStudied)
    if (!plan || !wizardStateRef) return
    const newPlan = replanFromProgress(plan, wizardStateRef, dayIndex, examName, pagesStudied)
    setPlan(newPlan)
    setEditingPages(null)
    setPagesInput('')
  }

  const handleToggleProgress = async (key: string) => {
    if (!isPremium) { setShowPremium(true); return }
    const next = { ...progress, [key]: !progress[key] }
    setProgress(next)
    if (user) {
      await supabase.from('plans').upsert({
        user_id: user.id,
        progress: next,
        updated_at: new Date().toISOString()
      }, { onConflict: 'user_id' })
    }
  }

  return (
    <>
      <style>{`
        @media (max-width: 600px) {
          .plan-header { padding: 10px 14px !important; }
          .plan-header h1 { font-size: 16px !important; }
          .plan-header p { display: none; }
          .plan-header-right { gap: 4px !important; }
          .plan-header-right .btn-premium-label { display: none; }
          .plan-actions { gap: 8px !important; flex-wrap: wrap !important; }
          .plan-actions button { padding: 8px 10px !important; font-size: 12px !important; flex: 1 1 calc(50% - 8px) !important; justify-content: center !important; min-width: 0 !important; }
          .plan-actions button .btn-label { display: inline !important; font-size: 12px !important; }
          .plan-table-header { display: none !important; }
          .plan-table-row { grid-template-columns: 1fr !important; padding: 12px 16px !important; gap: 4px !important; }
          .plan-table-row > div:first-child { display: flex !important; align-items: center !important; gap: 8px !important; margin-bottom: 4px !important; }
          .plan-col-time { display: none !important; }
          .plan-col-progress { margin-top: 6px !important; }
          .plan-table-row > div:nth-child(2) { font-size: 14px !important; }
          .plan-table-row > div:nth-child(3) { font-size: 13px !important; }
        }
        @media (max-width: 600px) {
          .plan-actions { gap: 6px !important; }
          .plan-actions button { padding: 7px 10px !important; font-size: 12px !important; }
          .plan-actions button .btn-label { display: none; }
          .plan-actions button .btn-icon { display: inline !important; }
          .plan-header { padding: 10px 14px !important; flex-wrap: wrap; gap: 8px; }
          .plan-header h1 { font-size: 17px !important; }
          .plan-header-right { gap: 6px !important; }
          .plan-header-right button { padding: 5px 10px !important; font-size: 12px !important; }
        }
      `}</style>
      {showPremium && <PremiumModal onClose={() => setShowPremium(false)} user={user} />}
      {showNameModal && (
        <div onClick={() => setShowNameModal(false)} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: 'white', borderRadius: 16, padding: 28, maxWidth: 360, width: '90%' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 700 }}>💾 Salva piano</h3>
            <input
              value={planName}
              onChange={e => setPlanName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && planName.trim()) { handleSavePlan(planName); setShowNameModal(false); setPlanName('') } }}
              placeholder="Es. Matematica + Fisica - Giugno"
              autoFocus
              style={{ width: '100%', border: '1px solid #E5E7EB', borderRadius: 8, padding: '10px 12px', fontSize: 14, boxSizing: 'border-box' as any, marginBottom: 12 }}
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => { if (planName.trim()) { handleSavePlan(planName); setShowNameModal(false); setPlanName('') } }}
                style={{ flex: 1, backgroundColor: '#1c3fff', color: 'white', border: 'none', borderRadius: 10, padding: '10px 0', cursor: 'pointer', fontWeight: 600, fontSize: 14 }}
              >Salva</button>
              <button onClick={() => setShowNameModal(false)} style={{ flex: 1, backgroundColor: '#F3F4F6', color: '#6B7280', border: 'none', borderRadius: 10, padding: '10px 0', cursor: 'pointer', fontSize: 14 }}>Annulla</button>
            </div>
          </div>
        </div>
      )}
      <div style={{ minHeight: "100vh", backgroundColor: "#F8F9FA" }}>
        <header className="plan-header" style={{ backgroundColor: "white", borderBottom: "1px solid #E5E7EB", padding: "12px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <style>{'.plan-short{display:none}@media(max-width:600px){.plan-full{display:none}.plan-short{display:inline}}'}</style>
<img src="/favicon.svg" alt="logo" style={{ width: 32, height: 32, flexShrink: 0 }} />
              <h1 style={{ fontSize: 18, fontWeight: 'bold', color: '#111827', margin: 0 }}>
                <span className="plan-full">Study Planner Schedule</span>
                <span className="plan-short">Study Planner</span>
              </h1>
            </div>
            
          </div>
          <div className="plan-header-right" style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {isPremium ? (
              <div style={{ fontSize: 13, fontWeight: 600, color: '#15803D', backgroundColor: '#F0FDF4', padding: '6px 14px', borderRadius: 20, border: '1px solid #86EFAC', display: 'flex', alignItems: 'center', gap: 6 }}>
                ✦ Premium ✓
              </div>
            ) : (
              <button onClick={() => setShowPremium(true)} style={{ fontSize: 13, fontWeight: 500, color: '#D97706', backgroundColor: '#FFFBEB', padding: '6px 12px', borderRadius: 20, border: '1px solid #FDE68A', cursor: 'pointer' }}>
                ✦ <span className="btn-premium-label">Premium</span>
              </button>
            )}
            <button onClick={() => navigate("/plans")} style={{ fontSize: 13, color: "#6B7280", backgroundColor: "white", border: "1px solid #E5E7EB", borderRadius: 8, padding: "8px 10px", cursor: "pointer" }}>
              📚 <span className="btn-label">Piani</span>
            </button>
            <button onClick={() => navigate("/")} style={{ fontSize: 13, color: "#6B7280", backgroundColor: "white", border: "1px solid #E5E7EB", borderRadius: 8, padding: "8px 10px", cursor: "pointer" }}>
              ←
            </button>
          </div>
        </header>

        <div style={{ maxWidth: 900, margin: "0 auto", padding: "24px 16px" }}>
          <div style={{ backgroundColor: stressColor, borderRadius: 12, padding: "12px 20px", marginBottom: 24, display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 20 }}>{stressEmoji}</span>
            <span style={{ fontWeight: 500, color: "#111827" }}>{stressText}</span>
          </div>

          <div className="plan-actions" style={{ display: "flex", gap: 8, marginBottom: 24, flexWrap: "nowrap", overflowX: "auto", paddingBottom: 4 }}>
            <button onClick={() => { if (!user) { navigate('/auth'); return } setShowNameModal(true) }} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 8, border: "1px solid #E5E7EB", backgroundColor: "white", cursor: "pointer", fontSize: 13, whiteSpace: "nowrap" }}>
              💾 <span className="btn-label">Salva</span>
            </button>
            <button onClick={handleExportICS} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 8, border: "1px solid #E5E7EB", backgroundColor: "white", cursor: "pointer", fontSize: 13, whiteSpace: "nowrap" }}>
              📅 <span className="btn-label">Calendario</span> <span style={{ fontSize: 11, color: "#D97706" }}>✦</span>
            </button>

            <button onClick={handleDownloadPDF} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 8, border: "1px solid #E5E7EB", backgroundColor: "white", cursor: "pointer", fontSize: 13, whiteSpace: "nowrap" }}>
              📄 <span className="btn-label">Scarica</span> <span style={{ fontSize: 11, color: "#D97706" }}>✦</span>
            </button>

          </div>

          <div style={{ backgroundColor: "white", borderRadius: 16, overflow: "hidden", border: "1px solid #E5E7EB" }}>
            <div className="plan-table-header" style={{ display: "grid", gridTemplateColumns: "130px 2fr 1.5fr 80px 140px", backgroundColor: "#F9FAFB", padding: "12px 20px", borderBottom: "1px solid #E5E7EB" }}>
              {[
                  { key: t("plan.columns.date"), cls: "" },
                  { key: t("plan.columns.subject"), cls: "" },
                  { key: t("plan.columns.pages"), cls: "plan-col-time" },
                  { key: t("plan.columns.time"), cls: "plan-col-time" },
                  { key: t("plan.columns.progress"), cls: "plan-col-progress" }
                ].map(col => (
                <div key={col.key} className={col.cls} style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 1 }}>{col.key}</div>
              ))}
            </div>
            {plan.days.map((day, i) => (
              <DayRow key={i} day={day} dayIndex={i} lang={i18n.language} t={t} onPremium={handlePremiumAction} progress={progress} onToggle={handleToggleProgress} isPremium={isPremium} editingPages={editingPages} pagesInput={pagesInput} setPagesInput={setPagesInput} setEditingPages={setEditingPages} onReplan={handleReplan} />
            ))}
          </div>
        </div>
      </div>
    </>
  )
}

function DayRow({ day, dayIndex, lang, t, onPremium, progress, onToggle, isPremium, editingPages, pagesInput, setPagesInput, setEditingPages, onReplan }: { day: DayPlan, dayIndex: number, lang: string, t: any, onPremium: () => void, progress: Record<string, boolean>, onToggle: (key: string) => void, isPremium: boolean, editingPages: any, pagesInput: string, setPagesInput: (v: string) => void, setEditingPages: (v: any) => void, onReplan: (dayIndex: number, examName: string, pages: number) => void }) {
  const dateStr = formatDate(day.date, lang)
  const today = isToday(day.date)

  if (day.isExamDay) {
    return (
      <div style={{ backgroundColor: day.examColor || "#1c3fff", padding: "14px 20px", textAlign: "center" }}>
        <span style={{ color: "white", fontWeight: "bold", fontSize: 15 }}>🎓 {t("plan.exam")}: {day.examName}</span>
      </div>
    )
  }

  if (day.isRestDay) {
    return (
      <div style={{ padding: "14px 20px", backgroundColor: "#F9FAFB", borderBottom: "1px solid #F3F4F6", display: "flex", alignItems: "center", gap: 16 }}>
        <div style={{ minWidth: 60 }}>
          <div style={{ fontSize: 13, color: "#9CA3AF" }}>{dateStr.split(" ")[0]}</div>
          <div style={{ fontSize: 16, fontWeight: "bold", color: "#9CA3AF" }}>{dateStr.split(" ").slice(1).join(" ")}</div>
        </div>
        <span style={{ color: "#9CA3AF" }}>🚫 {t("plan.restDay")}</span>
      </div>
    )
  }

  const totalMinutes = day.subjects.reduce((sum, s) => sum + (s.isRevision ? 0 : s.minutes), 0)

  return (
    <div style={{ borderBottom: "1px solid #F3F4F6" }}>
      {day.subjects.map((sub, j) => (
        <div key={j} className="plan-table-row" style={{ display: "grid", gridTemplateColumns: "130px 2fr 1.5fr 80px 140px", padding: "14px 20px", alignItems: "center" }}>
          <div>
            {j === 0 && (
              <>
                <div style={{ fontSize: 13, color: "#9CA3AF" }}>{dateStr.split(" ")[0]}</div>
                <div style={{ fontSize: 16, fontWeight: "bold", color: "#111827" }}>{dateStr.split(" ").slice(1).join(" ")}</div>
                {today && <div style={{ display: "inline-block", backgroundColor: "#1c3fff", color: "white", fontSize: 10, fontWeight: "bold", padding: "2px 8px", borderRadius: 10, marginTop: 2 }}>{t("plan.today")}</div>}
              </>
            )}
          </div>
          <div>
            <span style={{ color: sub.color, fontWeight: "bold", fontSize: 14 }}>{sub.examName}</span>
            {!sub.isRevision && <span style={{ color: "#9CA3AF", fontSize: 12, marginLeft: 6 }}>({formatMinutes(sub.minutes)})</span>}
          </div>
          <div style={{ fontSize: 14, color: "#111827" }}>
            {sub.isRevision
              ? <span style={{ color: "#6B7280" }}>📖 {t("plan.revision")}</span>
              : editingPages?.dayIndex === dayIndex && editingPages?.subIndex === j && editingPages?.examName === sub.examName
                ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input
                      type="number"
                      min="0"
                      max={sub.pages}
                      value={pagesInput}
                      onChange={e => setPagesInput(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && pagesInput) { const v = parseInt(pagesInput); setPagesInput(''); onReplan(dayIndex, sub.examName, v) } }}
                      autoFocus
                      placeholder={String(sub.pages)}
                      style={{ width: 60, border: '1px solid #1c3fff', borderRadius: 6, padding: '2px 6px', fontSize: 13 }}
                    />
                    <button onClick={() => { if (pagesInput) { const v = parseInt(pagesInput); setPagesInput(''); onReplan(dayIndex, sub.examName, v) } }} style={{ backgroundColor: '#1c3fff', color: 'white', border: 'none', borderRadius: 6, padding: '2px 8px', cursor: 'pointer', fontSize: 12 }}>✓</button>
                    <button onClick={() => setEditingPages(null)} style={{ backgroundColor: '#F3F4F6', color: '#6B7280', border: 'none', borderRadius: 6, padding: '2px 8px', cursor: 'pointer', fontSize: 12 }}>✕</button>
                  </div>
                )
                : (
                  <span
                    onClick={() => { const dayDate = new Date(day.date); const tomorrow = new Date(); tomorrow.setHours(0,0,0,0); tomorrow.setDate(tomorrow.getDate()+1); if (dayDate >= tomorrow) return; setEditingPages({ dayIndex, subIndex: j, examName: sub.examName }) }}
                    style={{ cursor: 'pointer' }}
                    title='Clicca per aggiornare le pagine studiate'
                  >
                    <strong>p. {sub.startPage}–{sub.endPage}</strong> <span style={{ color: "#9CA3AF", fontSize: 12 }}>({sub.pages} {t("plan.pages")})</span>
                    {(() => { const dayDate = new Date(day.date); const tomorrow = new Date(); tomorrow.setHours(0,0,0,0); tomorrow.setDate(tomorrow.getDate()+1); return dayDate < tomorrow ? <span style={{ fontSize: 10, color: '#1c3fff', marginLeft: 4 }}>✏️</span> : null })()}
                  </span>
                )
            }
          </div>
          <div className="plan-col-time" style={{ fontSize: 14, color: "#374151" }}>
            {j === 0 && totalMinutes > 0 ? formatMinutes(totalMinutes) : ""}
          </div>
          <div className="plan-col-progress" style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ flex: 1, height: 6, backgroundColor: "#F3F4F6", borderRadius: 3 }}>
              <div style={{ width: sub.progressPercent + "%", height: "100%", backgroundColor: sub.color, borderRadius: 3 }} />
            </div>
            <span style={{ fontSize: 12, color: "#6B7280", minWidth: 32 }}>{sub.progressPercent}%</span>
            {(() => {
              const key = day.date + "-" + sub.examName + "-" + j
              const done = progress[key]
              return (
                <button
                  onClick={() => onToggle(key)}
                  style={{ fontSize: 16, border: "none", backgroundColor: "transparent", cursor: "pointer", padding: "2px 4px", opacity: done ? 1 : 0.35, transition: "opacity 0.2s" }}
                  title={done ? "Completato" : "Segna come completato"}
                >
                  {done ? "✅" : "⬜"}
                </button>
              )
            })()}
          </div>
        </div>
      ))}
    </div>
  )
}
